import { clearCheckoutSession } from './checkoutSession.js';
import { subscribe, emit, events } from './eventManager.js';
import { fetchAppState, postAppState, fetchUserProfileInfo, fetchSession } from './apiUtils.js';
import NavigationService from './NavigationService.js';
const DEFAULT_AUTH_ENV = 'prod';
let account = {
    authenticated: false,
    user: undefined,
    profile: undefined,
    idToken: undefined,
    notifications: undefined,
    notificationSummary: undefined,
};
let error = false;
// store the authurl for prod right away incase the initializeAccount function doesn't get ran.
let authenticationUrl = getAuthUrl(DEFAULT_AUTH_ENV);
let extraProfileLinks = [];
const visitor = {
    country: null,
    ip: null,
    isBot: null,
    isInternal: null,
};
export async function initializeAccount(config = {}) {
    const { userRequired = false, redirectUrl = undefined, authEnv = DEFAULT_AUTH_ENV, checkCookiesDisabled = false } = config;
    authenticationUrl = getAuthUrl(authEnv);
    extraProfileLinks = Array.isArray(config.extraProfileLinks) ? config.extraProfileLinks : [];
    // set the account variable just incase it got messed up
    initializeAccountVariable();
    try {
        const { body, headers } = await fetchSession({ authenticationUrl, viewerUri: NavigationService.getCurrentUrl() });
        Object.assign(visitor, mapSessionHeadersToVisitor(headers));
        setVisitor();
        const { authenticated, csrfToken, sid, token: jwt, idToken, user } = body;
        if (userRequired && !authenticated) {
            login(redirectUrl);
        }
        account.authenticated = authenticated;
        account.sid = sid;
        account.csrfToken = csrfToken;
        account.idToken = idToken;
        if (checkCookiesDisabled) {
            // If the user is authenticated, we can assume cookies are not disabled.
            if (authenticated) {
                account.cookiesDisabled = false;
            }
            else {
                // If the user is not authenticated, check the session a second time to compare session IDs.
                // If the session IDs don't match, we can assume cookies are disabled.
                const { body } = await fetchSession({ authenticationUrl });
                const { sid: sidCheck } = body;
                account.cookiesDisabled = sid !== sidCheck;
            }
        }
        if (authenticated) {
            account.user = {
                ...user,
                jwt,
                picture: transformPictureUrl(user.picture, user.givenName, user.familyName),
            };
        }
        setAccount();
    }
    catch (error) {
        setAccount(error);
    }
}
function setAccount(error = false) {
    emit(events.ACCOUNT, account, error);
    // emit event for the depricated subscribeToUser
    emit(events.USER, account.user, error);
}
function setUser(user) {
    account.user = user;
    setAccount();
}
function setVisitor(error = false) {
    emit(events.VISITOR, visitor, error);
}
function subscribeToAccount(callback) {
    return subscribe(events.ACCOUNT, callback);
}
function subscribeToUser(callback) {
    return subscribe(events.USER, callback);
}
function subscribeToVisitor(callback) {
    return subscribe(events.VISITOR, callback);
}
/**
 * Get data from Session Manager's app state endpoint. This data persists for the duration of the user's session and
 * is namespaced to allow different applications to store their own data without conflicts. To use this in your app,
 * you need to set up the namespace in the Session Manager.
 * @param namespace A base64 encoded string for the namespace for your app.
 * @param authUrl This is the authentication URL for the app, which defaults to the one set in the account utility.
 * @returns A promise that resolves to the session response containing the app state data and headers.
 */
async function getAppState(namespace, authUrl = authenticationUrl) {
    return fetchAppState({ encodedApiKey: namespace, authenticationUrl: authUrl });
}
/**
 * Set data in Session Manager's app state endpoint. This data persists for the duration of the user's session and
 * is namespaced to allow different applications to store their own data without conflicts. To use this in your app,
 * you need to set up the namespace in the Session Manager.
 * @param state The state data to set, which can be an object or a string.
 * @param namespace A base64 encoded string for the namespace for your app.
 * @param authUrl This is the authentication URL for the app, which defaults to the one set in the account utility.
 * @returns A promise that resolves to the session response containing the updated app state data.
 */
async function setAppState(state, namespace, authUrl = authenticationUrl) {
    return postAppState({ state, encodedApiKey: namespace, authenticationUrl: authUrl });
}
// Notifications are deprecated. This can be removed once calling functions are removed.
async function loadUserNotifications() {
    account.notifications = [];
    error = null;
    emit(events.ACCOUNT, account, error);
    return;
}
// Notifications are deprecated. This can be removed once calling functions are removed.
async function loadUserNotificationSummary() {
    account.notificationSummary = [];
    error = null;
    emit(events.ACCOUNT, account, error);
    return;
}
// Notifications are deprecated. This can be removed once calling functions are removed.
async function dismissUserNotifications() {
    try {
        await loadUserNotificationSummary();
    }
    catch (err) {
        error = err;
    }
}
function loadProfile() {
    fetchUserProfileInfo({ authenticationUrl, jwt: account.user?.jwt })
        .then(profile => {
        account.profile = profile;
        error = null;
        emit(events.ACCOUNT, account, error);
    })
        .catch(err => (error = err));
}
function login(redirectUrl = NavigationService.getCurrentUrl()) {
    NavigationService.navigate(getLoginUrl(redirectUrl));
}
function academicLogin(redirectUrl = NavigationService.getCurrentUrl()) {
    NavigationService.navigate(getAcademicLoginUrl(redirectUrl));
}
function linkedinLogin(redirectUrl = NavigationService.getCurrentUrl()) {
    NavigationService.navigate(getLinkedinLoginUrl(redirectUrl));
}
function googleLogin(redirectUrl = NavigationService.getCurrentUrl()) {
    NavigationService.navigate(getGoogleLoginUrl(redirectUrl));
}
function logout(redirectUrl = NavigationService.getCurrentUrl()) {
    clearCheckoutSession();
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = getLogoutUrl(redirectUrl);
    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'csrfToken';
    csrfInput.value = account.csrfToken ?? '';
    form.appendChild(csrfInput);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
}
function createAccount(redirectUrl = NavigationService.getCurrentUrl()) {
    NavigationService.navigate(getCreateAccountUrl(redirectUrl));
}
function getLoginUrl(redirectUrl = NavigationService.getCurrentUrl()) {
    return `${authenticationUrl}/login?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getAcademicLoginUrl(redirectUrl = NavigationService.getCurrentUrl()) {
    return `${authenticationUrl}/login/academic?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getGoogleLoginUrl(redirectUrl = NavigationService.getCurrentUrl()) {
    return `${authenticationUrl}/login/google?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getLinkedinLoginUrl(redirectUrl = NavigationService.getCurrentUrl()) {
    return `${authenticationUrl}/login/linkedin?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getLogoutUrl(redirectUrl = NavigationService.getCurrentUrl()) {
    return `${authenticationUrl}/logout?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getCreateAccountUrl(redirectUrl = NavigationService.getCurrentUrl()) {
    return `${authenticationUrl}/login?view=signup&returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getExtraProfileLinks() {
    return extraProfileLinks;
}
function initializeAccountVariable() {
    account = {
        user: undefined,
        profile: undefined,
        notifications: undefined,
        notificationSummary: undefined,
    };
}
export function getAuthUrl(authEnv) {
    let authUrl = 'https://api.sw.siemens.com/auth'; // external siecom production
    if (authEnv === 'dev') {
        authUrl = 'https://api.stage.swi.siemens.com/auth';
    }
    return authUrl;
}
function mapSessionHeadersToVisitor(headers) {
    return {
        country: headers.visitorCountry,
        ip: headers.visitorIP,
        isBot: headers.visitorIsBot,
        isInternal: headers.visitorIsInternal,
    };
}
function transformPictureUrl(pictureUrl, givenName, familyName) {
    if (!pictureUrl)
        return pictureUrl;
    try {
        const url = new URL(pictureUrl);
        const initials = `${givenName?.[0] || ''}${familyName?.[0] || ''}`.toUpperCase();
        url.searchParams.set('d', 'initials');
        url.searchParams.set('initials', initials);
        return url.toString();
    }
    catch {
        // If URL parsing fails, return original
        return pictureUrl;
    }
}
/**
 * This is a function to return the current account object
 *
 * @return {Object} Account - An object containing account information
 *
 *  @example
 *
 *     getAccount()
 */
export function getAccount() {
    return account;
}
// Put the exports that we want to expose to the module on default
export default {
    subscribeToAccount,
    subscribeToUser,
    subscribeToVisitor,
    setUser,
    loadProfile,
    getAppState,
    setAppState,
    loadUserNotifications,
    loadUserNotificationSummary,
    dismissUserNotifications,
    login,
    academicLogin,
    linkedinLogin,
    googleLogin,
    logout,
    createAccount,
    getLoginUrl,
    getAcademicLoginUrl,
    getGoogleLoginUrl,
    getLinkedinLoginUrl,
    getLogoutUrl,
    getCreateAccountUrl,
    getAccount,
    getExtraProfileLinks,
};
